import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import { useCssHandles } from '../hooks/useCssHandles'
import { Layout, SEO } from '../shared/components'
import { BatteryCard } from '../modules/catalog/components'
import { useFeaturedBatteries } from '../modules/catalog/hooks'
import VehicleSearchWidget from '../components/VehicleSearchWidget'
import { useCart } from '../modules/cart/contexts/CartContext'
import { brandService } from '../services'
import type { Brand } from '../services/brandService'
import { buildImageUrl } from '../config'

const scroll = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`

const MarqueeContainer = styled.div`
  overflow: hidden;
  width: 100%;
  position: relative;
  
  &::before, &::after {
    content: "";
    position: absolute;
    top: 0;
    width: 100px;
    height: 100%;
    z-index: 2;
    pointer-events: none;
  }
  
  &::before {
    left: 0;
    background: linear-gradient(to right, white, transparent);
  }
  
  &::after {
    right: 0;
    background: linear-gradient(to left, white, transparent);
  }
`

const MarqueeTrack = styled.div`
  display: flex;
  width: max-content;
  animation: ${scroll} 40s linear infinite;
  
  &:hover {
    animation-play-state: paused;
  }
`

const CSS_HANDLES = [
  'home',
  'hero',
  'heroContent',
  'heroTitle',
  'heroSubtitle',
  'heroButton',
  'heroImage',
  'heroStats',
  'heroStat',
  'heroStatNumber',
  'heroStatLabel',
  'bannerRotativo',
  'bannerContainer',
  'bannerSlide',
  'bannerImage',
  'bannerContent',
  'bannerTitle',
  'bannerSubtitle',
  'bannerButton',
  'bannerDots',
  'bannerDot',
  'bannerPrev',
  'bannerNext',
  'featuredProducts',
  'featuredHeader',
  'featuredTitle',
  'featuredSubtitle',
  'featuredGrid',
  'featuredButton',
  'brands',
  'brandsHeader',
  'brandsTitle',
  'brandsGrid',
  'brandCard',
  'brandLogo',
  'benefits',
  'benefitsHeader',
  'benefitsTitle',
  'benefitsGrid',
  'benefitCard',
  'benefitIcon',
  'benefitTitle',
  'benefitDescription',
  'testimonials',
  'testimonialsHeader',
  'testimonialsTitle',
  'testimonialsGrid',
  'testimonialCard',
  'testimonialContent',
  'testimonialAuthor',
  'testimonialRating',
  'categories',
  'categoriesHeader',
  'categoriesTitle',
  'categoriesGrid',
  'categoryCard',
  'categoryImage',
  'categoryName',
  'categoryCount'
] as const

const Home: React.FC = () => {
  const handles = useCssHandles(CSS_HANDLES)
  const { batteries: featuredBatteries, loading: batteriesLoading } = useFeaturedBatteries()
  const { openCart } = useCart()
  const [currentSlide, setCurrentSlide] = useState(0)
  const [failedLogos, setFailedLogos] = useState<Record<string, boolean>>({})
  const [brands, setBrands] = useState<Brand[]>([])
  const [brandsLoading, setBrandsLoading] = useState(true)

  // Carregar marcas da API
  useEffect(() => {
    const loadBrands = async () => {
      try {
        setBrandsLoading(true)
        const response = await brandService.list({ limit: 12 })
        setBrands(response.brands)
      } catch (err) {
        console.error('Erro ao carregar marcas:', err)
        setBrands([])
      } finally {
        setBrandsLoading(false)
      }
    }

    void loadBrands()
  }, [])

  // Dados para o banner rotativo
  const bannerSlides = [
    {
      id: 1,
      imageDesktop: "/assets/banners/BANNER-1-DESKTOP.webp",
      imageMobile: "/assets/banners/BANNER-1-MOBILE.webp",
      alt: "Promoção de Baterias",
      link: "/baterias"
    },
    {
      id: 2,
      imageDesktop: "/assets/banners/BANNER-2-DESKTOP.webp",
      imageMobile: "/assets/banners/BANNER-2-MOBILE.webp",
      alt: "Entrega Rápida",
      link: "/baterias"
    },
    {
      id: 3,
      imageDesktop: "/assets/banners/BANNER-3-DESKTOP.webp",
      imageMobile: "/assets/banners/BANNER-3-MOBILE.webp",
      alt: "Melhores Marcas",
      link: "/baterias"
    }
  ]

  const handleAddToCart = (batteryId: string) => {
    // Abrir a sidebar do carrinho após adicionar o produto
    // batteryId é recebido do BatteryCard mas não é necessário aqui
    void batteryId // Marcar como usado intencionalmente
    openCart()
  }

  // Auto-play do banner
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % bannerSlides.length)
    }, 5000) // Muda a cada 5 segundos

    return () => clearInterval(timer)
  }, [bannerSlides.length])

  const goToSlide = (index: number) => {
    setCurrentSlide(index)
  }

  const goToPrevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + bannerSlides.length) % bannerSlides.length)
  }

  const goToNextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % bannerSlides.length)
  }

  const renderFeaturedSkeleton = () => (
    <div className="space-y-8" role="status" aria-live="polite" aria-busy="true">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {Array.from({ length: 8 }).map((_, index) => (
          <div
            key={`featured-skeleton-${index}`}
            className="space-y-4 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-6 shadow-sm"
            aria-hidden="true"
          >
            <div className="h-44 w-full rounded-xl bg-white/10 animate-pulse" />
            <div className="space-y-3">
              <div className="h-4 w-3/4 rounded bg-white/10 animate-pulse" />
              <div className="h-4 w-1/2 rounded bg-white/10 animate-pulse" />
            </div>
            <div className="flex items-center justify-between">
              <div className="h-6 w-20 rounded bg-white/10 animate-pulse" />
              <div className="h-10 w-24 rounded-lg bg-white/10 animate-pulse" />
            </div>
            <div className="h-10 w-full rounded-lg bg-white/10 animate-pulse" />
          </div>
        ))}
      </div>
    </div>
  )

  const renderFeaturedEmptyState = () => (
    <div className="rounded-3xl border border-dashed border-white/20 bg-white/5 backdrop-blur-sm px-10 py-16 text-center shadow-sm">
      <div className="mx-auto mb-8 flex h-16 w-16 items-center justify-center rounded-full bg-blue-500/20 text-yellow-400">
        <svg className="h-8 w-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15.362 5.214A8.252 8.252 0 0118 9.75c0 4.556-3.644 8.25-8.205 8.25A8.21 8.21 0 017.5 17.4M6 21l-3-3 3-3"
          />
        </svg>
      </div>
      <h3 className="mb-3 text-2xl font-semibold text-white">Em breve novidades por aqui</h3>
      <p className="mx-auto mb-6 max-w-2xl text-blue-100">
        No momento não temos produtos destacados, mas você pode explorar o catálogo completo e encontrar a bateria ideal
        para cada necessidade.
      </p>
      <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
        <Link
          to="/baterias"
          className="inline-flex items-center rounded-xl bg-yellow-500 px-6 py-3 text-sm font-bold text-blue-900 transition-all hover:bg-yellow-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 focus-visible:ring-offset-2"
        >
          Ver catálogo completo
        </Link>
      </div>
    </div>
  )


  return (
    <Layout>
      <SEO
        title="Home"
        description="Encontre a bateria ideal para seu carro, moto ou caminhão. Entrega e instalação rápida em toda a região."
      />
      <div className={`${handles.home}`}>
        <section className={`${handles.hero} relative text-white overflow-hidden pb-4`}>
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-600"></div>

          {/* Background Icon */}
          <div className="absolute top-1/2 -right-32 -translate-y-1/2 opacity-10 pointer-events-none hidden lg:block">
            <img
              src="/assets/icone-branco.svg"
              alt=""
              className="w-[550px] xl:w-[650px] object-contain animate-pulse"
            />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 py-6 lg:py-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className={`${handles.heroContent} relative z-10`}>
                <div className="mb-3">
                  <span className="inline-block bg-yellow-500 text-blue-900 px-5 py-2 rounded-full text-sm font-bold shadow-lg">
                    Energia em movimento
                  </span>
                </div>

                <h1 className={`${handles.heroTitle} text-4xl lg:text-6xl font-bold mb-3 leading-tight`}>
                  Energia de <span className="text-yellow-400">confiança</span>, potência no seu dia.
                </h1>

                <p className={`${handles.heroSubtitle} text-xl mb-5 text-blue-100 leading-relaxed`}>
                  Produtos de qualidade com entrega rápida.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <Link
                    to="/baterias"
                    className={`${handles.heroButton} bg-yellow-500 hover:bg-yellow-400 text-blue-900 font-bold px-10 py-4 rounded-xl transition-all duration-300 text-center transform hover:scale-105 shadow-xl hover:shadow-2xl text-lg`}
                  >
                    COMPRAR AGORA
                  </Link>
                </div>

                {/* Stats de Confiança */}
                <div className={`${handles.heroStats} grid grid-cols-3 gap-6`}>
                  <div className={`${handles.heroStat} text-center`}>
                    <div className={`${handles.heroStatNumber} text-2xl font-bold text-yellow-400`}>200+</div>
                    <div className={`${handles.heroStatLabel} text-sm text-blue-200`}>Modelos</div>
                  </div>
                  <div className={`${handles.heroStat} text-center`}>
                    <div className={`${handles.heroStatNumber} text-2xl font-bold text-yellow-400`}>Entrega</div>
                    <div className={`${handles.heroStatLabel} text-sm text-blue-200`}>Rápida</div>
                  </div>
                  <div className={`${handles.heroStat} text-center flex flex-col items-center`}>
                    <div className="flex items-center justify-center gap-2">
                      <span className={`${handles.heroStatNumber} text-2xl font-bold text-yellow-400`}>Garantia Segura</span>
                      <svg className="w-6 h-6 text-green-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div >
            </div >
          </div >
        </section >

        {/* Banner Rotativo */}
        <section className={`${handles.bannerRotativo} relative mt-6 mb-6`}>
          <div className="max-w-7xl mx-auto px-4">
            <div className={`${handles.bannerContainer} relative rounded-2xl overflow-hidden shadow-xl group grid grid-cols-1 min-h-[160px] md:min-h-[200px]`}>
              {bannerSlides.map((slide, index) => (
                <div
                  key={slide.id}
                  className={`${handles.bannerSlide} col-start-1 row-start-1 transition-opacity duration-1000 ${index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
                    }`}
                >
                  <Link to={slide.link} className="block w-full">
                    <picture className="block w-full">
                      <source media="(min-width: 768px)" srcSet={slide.imageDesktop} />
                      <img
                        src={slide.imageMobile}
                        alt={slide.alt}
                        className={`${handles.bannerImage} w-full h-auto object-contain block`}
                      />
                    </picture>
                  </Link>
                </div>
              ))}

              {/* Botões de navegação */}
              <button
                onClick={goToPrevSlide}
                className={`${handles.bannerPrev} absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all duration-300 backdrop-blur-sm z-20 opacity-0 group-hover:opacity-100`}
                aria-label="Slide anterior"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <button
                onClick={goToNextSlide}
                className={`${handles.bannerNext} absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all duration-300 backdrop-blur-sm z-20 opacity-0 group-hover:opacity-100`}
                aria-label="Próximo slide"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {/* Indicadores de posição */}
              <div className={`${handles.bannerDots} absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20`}>
                {bannerSlides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`${handles.bannerDot} w-2 h-2 rounded-full transition-all duration-300 ${index === currentSlide
                      ? 'bg-yellow-500 scale-125'
                      : 'bg-white/50 hover:bg-white/70'
                      }`}
                    aria-label={`Ir para slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section >

        {/* Seção Introdutória - Busca Inteligente */}
        <section className="py-12 bg-gradient-to-br from-blue-50 to-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-white px-6 py-3 rounded-full shadow-md mb-4">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <span className="text-sm font-semibold text-gray-700">Busca Inteligente por Veículo</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                Encontre a bateria <span className="text-blue-600">perfeita</span> para seu veículo
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Nossa tecnologia utiliza dados da Tabela FIPE para garantir compatibilidade 100% precisa com seu carro, moto ou caminhão
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-8">
              <div className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 text-sm">Resultado Rápido</div>
                  <div className="text-xs text-gray-500">Em segundos</div>
                </div>
              </div>

              <div className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm">
                <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 text-sm">Melhor Preço</div>
                  <div className="text-xs text-gray-500">Garantido</div>
                </div>
              </div>

              <div className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 text-sm">Compatibilidade</div>
                  <div className="text-xs text-gray-500">100% Preciso</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Widget de Busca por Veículo */}
        <section className="pt-0 pb-12 bg-gradient-to-br -mt-12">
          <div className="max-w-7xl mx-auto px-4">
            <VehicleSearchWidget />
          </div>
        </section>

        {/* Produtos em Destaque */}
        <section className={`${handles.featuredProducts} py-24 bg-gradient-to-r from-blue-900 to-blue-800 text-white relative overflow-hidden`}>
          {/* Background Icon */}
          <div className="absolute top-1/2 -left-32 -translate-y-1/2 opacity-5 pointer-events-none hidden lg:block">
            <img
              src="/assets/icone-branco.svg"
              alt=""
              className="w-[1000px] object-contain"
            />
          </div>

          <div className="max-w-7xl mx-auto px-4 relative z-10">
            <div className={`${handles.featuredHeader} text-center mb-16`}>
              <div className="inline-block bg-yellow-500 text-blue-900 px-6 py-3 rounded-full text-sm font-bold mb-6 shadow-lg">
                PRODUTOS EM DESTAQUE
              </div>
              <h2 className={`${handles.featuredTitle} text-4xl font-bold mb-4`}>
                As <span className="text-yellow-400">Mais Vendidas</span>
              </h2>
              <p className={`${handles.featuredSubtitle} text-xl text-blue-100 max-w-2xl mx-auto`}>
                Produtos com melhor custo-benefício e maior satisfação dos clientes
              </p>
            </div>

            {/* Loading State */}
            {batteriesLoading && (
              renderFeaturedSkeleton()
            )}

            {/* Empty State */}
            {!batteriesLoading && (!featuredBatteries || featuredBatteries.length === 0) && (
              renderFeaturedEmptyState()
            )}

            {/* Batteries Grid */}
            {!batteriesLoading && featuredBatteries && featuredBatteries.length > 0 && (
              <div className={`${handles.featuredGrid} grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8`}>
                {featuredBatteries.slice(0, 8).map((battery) => (
                  <BatteryCard
                    key={battery.id}
                    battery={battery}
                    onAddToCart={handleAddToCart}
                  />
                ))}
              </div>
            )}

            <div className="text-center mt-16">
              <Link
                to="/baterias"
                className={`${handles.featuredButton} bg-yellow-500 hover:bg-yellow-400 text-blue-900 font-bold px-12 py-4 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl text-lg`}
              >
                Ver Todas as Baterias
              </Link>
            </div>
          </div>
        </section >

        {/* Marcas Parceiras */}
        <section className={`${handles.brands} py-20 bg-white`}>
          <div className="max-w-7xl mx-auto px-4">
            <div className={`${handles.brandsHeader} text-center mb-16`}>
              <h2 className={`${handles.brandsTitle} text-4xl font-bold text-gray-900 mb-4`}>
                Marcas <span className="text-blue-600">Confiáveis</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Trabalhamos apenas com as melhores marcas do mercado
              </p>
            </div>
          </div>

          <div className="w-full">
            {brandsLoading ? (
              <div className="max-w-7xl mx-auto px-4">
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 justify-center items-center">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div key={`brand-skeleton-${index}`} className="bg-gray-50 rounded-xl p-6 h-32 animate-pulse border border-gray-100" />
                  ))}
                </div>
              </div>
            ) : brands.length > 0 ? (
              <MarqueeContainer>
                <MarqueeTrack>
                  {[...brands, ...brands].map((brand, index) => {
                    const logoUrl = buildImageUrl(`/uploads/brands/${brand.slug}.webp?t=${brand.updatedAt}`)
                    return (
                      <div key={`${brand.id}-${index}`} className={`${handles.brandCard} group mx-4 w-48 h-32 flex-shrink-0`}>
                        <Link to={`/catalogo?marca=${brand.slug}`} className="block h-full">
                          <div className="bg-gray-50 rounded-xl p-6 h-full flex items-center justify-center text-center hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 border border-gray-200">
                            <div className={`${handles.brandLogo} w-full h-full flex items-center justify-center`}>
                              {!failedLogos[brand.id] && logoUrl ? (
                                <img
                                  src={logoUrl}
                                  alt={brand.name}
                                  className="max-w-full max-h-full object-contain transition-all duration-300"
                                  onError={() => setFailedLogos(prev => ({ ...prev, [brand.id]: true }))}
                                />
                              ) : (
                                <span className="text-xl font-bold text-gray-700">{brand.name}</span>
                              )}
                            </div>
                          </div>
                        </Link>
                      </div>
                    )
                  })}
                </MarqueeTrack>
              </MarqueeContainer>
            ) : (
              <div className="text-center py-10 text-gray-500">
                Nenhuma marca disponível no momento.
              </div>
            )}
          </div>
        </section>

        {/* Benefícios e Diferenciais */}
        <section className={`${handles.benefits} py-20 bg-gradient-to-br from-white via-blue-50 to-yellow-50`}>
          <div className="max-w-7xl mx-auto px-4">
            <div className={`${handles.benefitsHeader} text-center mb-16`}>
              <div className="inline-block bg-blue-100 text-blue-800 px-6 py-3 rounded-full text-sm font-bold mb-6">
                DIFERENCIAIS
              </div>
              <h2 className={`${handles.benefitsTitle} text-4xl md:text-5xl font-bold text-gray-900 mb-4`}>
                Por que escolher a <span className="text-blue-600">Baterpower</span>?
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Nossos diferenciais que fazem a diferença na sua compra
              </p>
            </div>

            <div className={`${handles.benefitsGrid} grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8`}>
              <div className={`${handles.benefitCard} bg-white rounded-2xl p-8 text-center hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 group`}>
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className={`${handles.benefitTitle} text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors`}>
                  Entrega Rápida
                </h3>
                <p className={`${handles.benefitDescription} text-gray-600 leading-relaxed`}>
                  Entrega em até 24h para a região metropolitana
                </p>
              </div>

              <div className={`${handles.benefitCard} bg-white rounded-2xl p-8 text-center hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 group`}>
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h3 className={`${handles.benefitTitle} text-xl font-bold text-gray-900 mb-3 group-hover:text-green-600 transition-colors`}>
                  Garantia com fabricante
                </h3>
                <p className={`${handles.benefitDescription} text-gray-600 leading-relaxed`}>
                  Produtos de Confiança
                </p>
              </div>

              <div className={`${handles.benefitCard} bg-white rounded-2xl p-8 text-center hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 group`}>
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className={`${handles.benefitTitle} text-xl font-bold text-gray-900 mb-3 group-hover:text-yellow-600 transition-colors`}>
                  Preços Acessíveis
                </h3>
                <p className={`${handles.benefitDescription} text-gray-600 leading-relaxed`}>
                  Os melhores preços do mercado com qualidade garantida
                </p>
              </div>

              <div className={`${handles.benefitCard} bg-white rounded-2xl p-8 text-center hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 group`}>
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <h3 className={`${handles.benefitTitle} text-xl font-bold text-gray-900 mb-3 group-hover:text-purple-600 transition-colors`}>
                  Suporte Técnico
                </h3>
                <p className={`${handles.benefitDescription} text-gray-600 leading-relaxed`}>
                  Suporte especializado para escolher a bateria ideal
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Depoimentos */}
        <section className={`${handles.testimonials} py-20 bg-gradient-to-br from-blue-50 via-white to-yellow-50`}>
          <div className="max-w-7xl mx-auto px-4">
            <div className={`${handles.testimonialsHeader} text-center mb-16`}>
              <div className="inline-block bg-yellow-100 text-yellow-800 px-6 py-3 rounded-full text-sm font-bold mb-6">
                DEPOIMENTOS
              </div>
              <h2 className={`${handles.testimonialsTitle} text-4xl md:text-5xl font-bold text-gray-900 mb-4`}>
                O que nossos <span className="text-blue-600">clientes</span> dizem
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Mais de 10.000 clientes satisfeitos em todo o Brasil
              </p>
            </div>

            <div className={`${handles.testimonialsGrid} grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8`}>
              <div className={`${handles.testimonialCard} bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 relative`}>
                <div className={`${handles.testimonialRating} flex mb-4 relative z-10`}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className={`${handles.testimonialContent} text-gray-700 mb-6 text-lg leading-relaxed relative z-10`}>
                  Rápido atendimento, levou menos de uma hora entre a solicitação e troca da bateria. Fiz o orçamento em diversos lugares e de fato foi o mais barato da região. Bateria Moura 60Ah adquirida, com termo de garantia.
                </p>
                <div className={`${handles.testimonialAuthor} flex items-center gap-3 pt-4 border-t border-gray-100 relative z-10`}>
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold text-lg">
                    MS
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">Michel Santos</div>
                    <div className="text-sm text-gray-500">Campinas, SP</div>
                  </div>
                </div>
              </div>

              <div className={`${handles.testimonialCard} bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 relative`}>
                <div className={`${handles.testimonialRating} flex mb-4 relative z-10`}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className={`${handles.testimonialContent} text-gray-700 mb-6 text-lg leading-relaxed relative z-10`}>
                  Excelente Atendimento e Qualidade! Muito prestativos e atenciosos. Preço justo. Recomendo!
                </p>
                <div className={`${handles.testimonialAuthor} flex items-center gap-3 pt-4 border-t border-gray-100 relative z-10`}>
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center text-white font-bold text-lg">
                    EF
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">Elmar Farias</div>
                    <div className="text-sm text-gray-500">Campinas, SP</div>
                  </div>
                </div>
              </div>

              <div className={`${handles.testimonialCard} bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 relative`}>
                <div className={`${handles.testimonialRating} flex mb-4 relative z-10`}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className={`${handles.testimonialContent} text-gray-700 mb-6 text-lg leading-relaxed relative z-10`}>
                  Atendimento ágil, ótimos profissionais, usei os serviços a primeira vez e fiquei super satisfeita, além do preço ser bem compatível com o mercado.
                </p>
                <div className={`${handles.testimonialAuthor} flex items-center gap-3 pt-4 border-t border-gray-100 relative z-10`}>
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white font-bold text-lg">
                    RT
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">Rezma Tecnologia</div>
                    <div className="text-sm text-gray-500">Campinas, SP</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  )
}

export default Home
